import { useState, useMemo } from 'react'
import { InfoTip } from '../../components/InfoTip'
import { useExecutions } from '../../context/ExecutionContext'
import { useAuth } from '../../context/AuthContext'
import { useToast } from '../../context/ToastContext'
import { exportToExcel } from '../../utils/exportExcel'
import { MobileCard, MobileOnly, DesktopOnly } from '../../components/MobileCard'

const strategyMeta: Record<string, { label: string; icon: string; color: string }> = {
  rsi_straddle: { label: 'RSI Straddle', icon: '⟐', color: '#38bdf8' },
  trending_expiry: { label: 'Trending Expiry', icon: '⏱', color: '#a78bfa' },
}

export function TraderReports() {
  const { executions, trades, panicExit } = useExecutions()
  const { session } = useAuth()
  const { addToast } = useToast()

  const traderId = session?.user.id || 'trader-1'
  const traderUsername = session?.user.username || 'trader1'
  const [panicModal, setPanicModal] = useState<{ level: 'all' | 'strategy'; target: string } | null>(null)
  const [fromDate, setFromDate] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 30); return d.toISOString().split('T')[0]
  })
  const [toDate, setToDate] = useState(() => new Date().toISOString().split('T')[0])

  const handlePanicConfirm = () => {
    if (!panicModal) return
    if (panicModal.level === 'all') {
      panicExit('all')
      addToast('Panic exit — ALL positions squared off', 'warning', 6000)
    } else {
      const stratExecs = myExecs.filter(e => e.strategyType === panicModal.target && e.status === 'running')
      stratExecs.forEach(e => panicExit('set', e.id))
      addToast(`Panic exit — ${panicModal.target === 'rsi_straddle' ? 'RSI Straddle' : 'Trending Expiry'} positions squared off`, 'warning', 6000)
    }
    setPanicModal(null)
  }

  const myExecs = useMemo(() =>
    executions.filter((e) => e.traderId === traderId || e.traderId === traderUsername || e.traderId === 'trader1'),
    [executions, traderId, traderUsername]
  )

  const myTrades = useMemo(() =>
    trades.filter((t) => {
      if (!myExecs.some((e) => e.id === t.executionId)) return false
      const tradeDate = t.timestamp.split('T')[0]
      if (fromDate && tradeDate < fromDate) return false
      if (toDate && tradeDate > toDate) return false
      return true
    }),
    [trades, myExecs, fromDate, toDate]
  )

  const stratSummary = useMemo(() => {
    const types = ['rsi_straddle', 'trending_expiry'] as const
    return types.map((type) => {
      const execs = myExecs.filter((e) => e.strategyType === type)
      const typeTrades = myTrades.filter((t) => execs.some((e) => e.id === t.executionId))
      const totalPnl = execs.reduce((s, e) => s + (e.state.mtm?.totalPnl || 0), 0)
      const running = execs.filter((e) => e.status === 'running').length
      const entries = typeTrades.filter((t) => t.tradeType === 'entry' || t.tradeType === 'reentry').length
      const exits = typeTrades.filter((t) => t.tradeType.startsWith('exit')).length
      const slHits = typeTrades.filter((t) => t.tradeType === 'exit_sl').length
      const targetHits = typeTrades.filter((t) => t.tradeType === 'exit_target').length
      return { type, meta: strategyMeta[type], execs, trades: typeTrades, totalPnl, running, entries, exits, slHits, targetHits }
    })
  }, [myExecs, myTrades])

  const [stratFilter, setStratFilter] = useState<string>('all')

  const filteredTrades = useMemo(() => {
    if (stratFilter === 'all') return myTrades
    return myTrades.filter((t) => myExecs.find((e) => e.id === t.executionId)?.strategyType === stratFilter)
  }, [myTrades, myExecs, stratFilter])

  const hasRunning = myExecs.some(e => e.status === 'running')

  return (
    <div>
      <div className="page-head">
        <h1>My Reports</h1>
        <p className="page-sub">Your strategy-wise performance and transaction history</p>
        <div style={{ marginTop: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
            <label style={{ fontSize: '0.6875rem', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>From</label>
            <input type="date" className="cfg-input" style={{ width: 'auto', padding: '0.25rem 0.4rem', fontSize: '0.75rem' }} value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
            <label style={{ fontSize: '0.6875rem', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>To</label>
            <input type="date" className="cfg-input" style={{ width: 'auto', padding: '0.25rem 0.4rem', fontSize: '0.75rem' }} value={toDate} onChange={(e) => setToDate(e.target.value)} />
          </div>
          <div style={{ display: 'flex', gap: '0.25rem' }}>
            <button className="btn btn-sm btn-ghost" style={{ padding: '0.2rem 0.5rem', fontSize: '0.6875rem' }} onClick={() => { const d = new Date(); d.setDate(d.getDate() - 7); setFromDate(d.toISOString().split('T')[0]); setToDate(new Date().toISOString().split('T')[0]) }}>7d</button>
            <button className="btn btn-sm btn-ghost" style={{ padding: '0.2rem 0.5rem', fontSize: '0.6875rem' }} onClick={() => { const d = new Date(); d.setDate(d.getDate() - 30); setFromDate(d.toISOString().split('T')[0]); setToDate(new Date().toISOString().split('T')[0]) }}>30d</button>
            <button className="btn btn-sm btn-ghost" style={{ padding: '0.2rem 0.5rem', fontSize: '0.6875rem' }} onClick={() => { const d = new Date(); d.setDate(d.getDate() - 90); setFromDate(d.toISOString().split('T')[0]); setToDate(new Date().toISOString().split('T')[0]) }}>90d</button>
          </div>
          <button className="btn btn-sm btn-ghost" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.375rem' }}
            onClick={() => {
              exportToExcel(myTrades.map(t => {
                const exec = myExecs.find(e => e.id === t.executionId)
                return {
                  Time: new Date(t.timestamp).toLocaleString(), Set: exec?.setName || '', Strategy: t.strategyType,
                  Underlying: t.underlying, Expiry: t.expiry, Strike: t.strike, OptionType: t.optionType,
                  TradeType: t.tradeType, EntryPrice: t.entryPrice, ExitPrice: t.exitPrice ?? '',
                  Qty: t.qty, PnL: t.pnl ?? '', Reason: t.reason || '',
                }
              }), `Aalap_MyTrades_${new Date().toISOString().split('T')[0]}`)
              addToast('Excel exported successfully', 'success')
            }}>
            ⬇ Export
          </button>
        </div>
      </div>

      {/* Panic strip */}
      {hasRunning && (
        <div className="dash-control-strip" style={{ marginBottom: '1.25rem' }}>
          <div className="dash-mtm-strip">
            <div className="mtm-item">
              <span className="mtm-label">Running Sets</span>
              <span className="mtm-val">{myExecs.filter(e => e.status === 'running').length}</span>
            </div>
            <div className="mtm-item mtm-item-divider" />
            <div className="mtm-item">
              <span className="mtm-label">Total MTM <InfoTip term="mtm" /></span>
              <span className={`mtm-val ${myExecs.reduce((s, e) => s + (e.state.mtm?.totalPnl || 0), 0) >= 0 ? 'green' : 'red'}`}>
                {myExecs.reduce((s, e) => s + (e.state.mtm?.totalPnl || 0), 0) >= 0 ? '+' : ''}₹{myExecs.reduce((s, e) => s + (e.state.mtm?.totalPnl || 0), 0).toLocaleString()}
              </span>
            </div>
          </div>
          <div className="dash-panic-zone">
            <button className="btn btn-red btn-sm" onClick={() => setPanicModal({ level: 'all', target: 'ALL' })}>
              ⚠ Panic All
            </button>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
        {stratSummary.map((s) => {
          if (s.execs.length === 0) return null
          const pnlColor = s.totalPnl >= 0 ? 'var(--green)' : 'var(--red)'
          const winRate = s.exits > 0 ? ((s.targetHits / s.exits) * 100).toFixed(0) : '—'
          return (
            <div key={s.type} className="card">
              <div className="card-header">
                <span className="card-title" style={{ color: s.meta.color }}>{s.meta.icon} {s.meta.label}</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{s.execs.length} sets · {s.running} running</span>
                  {s.running > 0 && (
                    <button className="btn btn-sm btn-red" onClick={() => setPanicModal({ level: 'strategy', target: s.type })}>
                      ⚠ Panic {s.meta.label.split(' ')[0]}
                    </button>
                  )}
                </div>
              </div>

              <div className="stats-grid" style={{ marginBottom: '1.25rem' }}>
                <div className="stats-card" style={{ '--card-accent': pnlColor } as React.CSSProperties}>
                  <div className="stats-card-top"><span className="stats-title">Total P&L <InfoTip term="pnl" /></span></div>
                  <div className="stats-value-wrap"><span className="stats-value" style={{ color: pnlColor, WebkitTextFillColor: pnlColor }}>{s.totalPnl >= 0 ? '+' : ''}₹{s.totalPnl.toLocaleString()}</span></div>
                </div>
                <div className="stats-card" style={{ '--card-accent': 'var(--accent)' } as React.CSSProperties}>
                  <div className="stats-card-top"><span className="stats-title">Win Rate <InfoTip term="winRate" /></span></div>
                  <div className="stats-value-wrap"><span className="stats-value">{winRate}{winRate !== '—' ? '%' : ''}</span></div>
                  <div className="stats-subtitle">{s.targetHits} targets · {s.slHits} SL hits</div>
                </div>
                <div className="stats-card" style={{ '--card-accent': 'var(--purple)' } as React.CSSProperties}>
                  <div className="stats-card-top"><span className="stats-title">Activity</span></div>
                  <div className="stats-value-wrap"><span className="stats-value">{s.entries}</span></div>
                  <div className="stats-subtitle">entries · {s.exits} exits</div>
                </div>
              </div>

              <h4 style={{ fontSize: '0.75rem', color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: '0.5rem', letterSpacing: '0.04em' }}>
                Recent Transactions <InfoTip term="transactionLog" /> <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>(last 10)</span>
              </h4>
              {s.trades.length === 0 ? (
                <p style={{ color: 'var(--text-muted)', fontSize: '0.8125rem' }}>No transactions yet.</p>
              ) : (
                <>
                  <DesktopOnly>
                    <div className="table-wrap">
                      <table>
                        <thead>
                          <tr><th>Time</th><th>Set</th><th>Underlying</th><th>Expiry</th><th>Strike</th><th>Type</th><th>Option</th><th>Entry</th><th>Exit</th><th>Qty</th><th>P&L <InfoTip term="pnl" /></th><th>Reason</th></tr>
                        </thead>
                        <tbody>
                          {s.trades.slice(-10).reverse().map((t) => {
                            const exec = myExecs.find((e) => e.id === t.executionId)
                            const ep = exec?.params as any
                            return (
                              <tr key={t.id}>
                                <td style={{ fontSize: '0.6875rem', color: 'var(--text-muted)' }}>{new Date(t.timestamp).toLocaleString()}</td>
                                <td><strong>{exec?.setName || '—'}</strong></td>
                                <td>{exec?.underlying || '—'}</td>
                                <td><span className="badge badge-entry" style={{ fontSize: '0.6875rem' }}>{ep?.expiryType || '—'}</span></td>
                                <td><code style={{ fontSize: '0.6875rem' }}>{ep?.strikeSelectionMode || '—'}</code></td>
                                <td><span className={`badge ${t.tradeType.startsWith('exit') ? 'badge-sl' : t.tradeType === 'entry' ? 'badge-target' : 'badge-entry'}`}>{t.tradeType.replace('_', ' ')}</span></td>
                                <td><span style={{ color: t.optionType === 'CE' ? 'var(--green)' : 'var(--red)', fontWeight: 600 }}>{t.optionType}</span></td>
                                <td>₹{t.entryPrice}</td>
                                <td>{t.exitPrice !== undefined ? `₹${t.exitPrice}` : '—'}</td>
                                <td>{t.quantity}</td>
                                <td style={{ color: t.pnl !== undefined ? (t.pnl >= 0 ? 'var(--green)' : 'var(--red)') : 'var(--text-muted)', fontWeight: 600 }}>
                                  {t.pnl !== undefined ? `${t.pnl >= 0 ? '+' : ''}₹${t.pnl}` : '—'}
                                </td>
                                <td style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{t.reason || '—'}</td>
                              </tr>
                            )
                          })}
                        </tbody>
                      </table>
                    </div>
                  </DesktopOnly>
                  <MobileOnly>
                    <div className="m-card-list">
                      {s.trades.slice(-10).reverse().map((t) => {
                        const exec = myExecs.find((e) => e.id === t.executionId)
                        const ep = exec?.params as any
                        const pnlVal = t.pnl
                        const hasPnl = pnlVal !== undefined
                        return (
                          <MobileCard
                            key={t.id}
                            title={`${exec?.underlying || '—'} ${ep?.strikeSelectionMode || ''}`}
                            subtitle={`${s.meta.label} · ${new Date(t.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`}
                            status={hasPnl ? {
                              label: `${pnlVal >= 0 ? '+' : ''}₹${pnlVal}`,
                              variant: pnlVal >= 0 ? 'green' : 'red',
                            } : {
                              label: 'Open',
                              variant: 'muted',
                            }}
                            fields={[
                              { label: 'Type', value: t.tradeType.replace(/_/g, ' '), bold: true },
                              { label: 'Option', value: t.optionType, accent: t.optionType === 'CE' ? 'green' : 'red', bold: true },
                              { label: 'Entry', value: `₹${t.entryPrice}` },
                              { label: 'Exit', value: t.exitPrice !== undefined ? `₹${t.exitPrice}` : '—' },
                            ]}
                          />
                        )
                      })}
                    </div>
                  </MobileOnly>
                </>
              )}
            </div>
          )
        })}
        {stratSummary.every((s) => s.execs.length === 0) && (
          <div className="card" style={{ textAlign: 'center', padding: '2rem' }}>
            <p style={{ color: 'var(--text-muted)' }}>No execution sets assigned to you yet.</p>
          </div>
        )}
      </div>

      <div className="card" style={{ marginTop: '1.5rem' }}>
        <div className="card-header">
          <span className="card-title">◇ All My Transactions <InfoTip term="transactionLog" /></span>
          <div className="filter-group" style={{ minWidth: '140px' }}>
            <select className="cfg-input" value={stratFilter} onChange={(e) => setStratFilter(e.target.value)}>
              <option value="all">All Strategies</option>
              <option value="rsi_straddle">RSI Straddle</option>
              <option value="trending_expiry">Trending Expiry</option>
            </select>
          </div>
        </div>
        {filteredTrades.length === 0 ? (
          <div style={{ padding: '1.5rem', textAlign: 'center', color: 'var(--text-muted)' }}>No transactions.</div>
        ) : (
          <>
            <DesktopOnly>
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr><th>Time</th><th>Set</th><th>Strategy</th><th>Underlying</th><th>Expiry</th><th>Strike</th><th>Type</th><th>Option</th><th>Entry</th><th>Exit</th><th>Qty</th><th>P&L <InfoTip term="pnl" /></th><th>Reason</th></tr>
                  </thead>
                  <tbody>
                    {filteredTrades.slice().reverse().map((t) => {
                      const exec = myExecs.find((e) => e.id === t.executionId)
                      const meta = exec ? strategyMeta[exec.strategyType] : null
                      const ep = exec?.params as any
                      return (
                        <tr key={t.id}>
                          <td style={{ fontSize: '0.6875rem', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{new Date(t.timestamp).toLocaleString()}</td>
                          <td><strong>{exec?.setName || '—'}</strong></td>
                          <td>{meta ? <span style={{ color: meta.color }}>{meta.icon}</span> : '—'}</td>
                          <td>{exec?.underlying || '—'}</td>
                          <td><span className="badge badge-entry" style={{ fontSize: '0.6875rem' }}>{ep?.expiryType || '—'}</span></td>
                          <td><code style={{ fontSize: '0.6875rem' }}>{ep?.strikeSelectionMode || '—'}</code></td>
                          <td><span className={`badge ${t.tradeType.startsWith('exit') ? 'badge-sl' : t.tradeType === 'entry' ? 'badge-target' : t.tradeType === 'reentry' ? 'badge-entry' : 'badge-exit'}`}>{t.tradeType.replace(/_/g, ' ')}</span></td>
                          <td><span style={{ color: t.optionType === 'CE' ? 'var(--green)' : 'var(--red)', fontWeight: 600 }}>{t.optionType}</span></td>
                          <td style={{ fontFamily: "'JetBrains Mono', monospace" }}>₹{t.entryPrice}</td>
                          <td style={{ fontFamily: "'JetBrains Mono', monospace" }}>{t.exitPrice !== undefined ? `₹${t.exitPrice}` : '—'}</td>
                          <td>{t.quantity}</td>
                          <td style={{ color: t.pnl !== undefined ? (t.pnl >= 0 ? 'var(--green)' : 'var(--red)') : 'var(--text-muted)', fontFamily: "'JetBrains Mono', monospace", fontWeight: 600 }}>
                            {t.pnl !== undefined ? `${t.pnl >= 0 ? '+' : ''}₹${t.pnl}` : '—'}
                          </td>
                          <td style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{t.reason || '—'}</td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </DesktopOnly>
            <MobileOnly>
              <div className="m-card-list">
                {filteredTrades.slice().reverse().map((t) => {
                  const exec = myExecs.find((e) => e.id === t.executionId)
                  const meta = exec ? strategyMeta[exec.strategyType] : null
                  const ep = exec?.params as any
                  const pnlVal = t.pnl
                  const hasPnl = pnlVal !== undefined
                  return (
                    <MobileCard
                      key={t.id}
                      title={`${exec?.underlying || '—'} ${ep?.strikeSelectionMode || ''}`}
                      subtitle={`${meta ? meta.label : '—'} · ${new Date(t.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`}
                      status={hasPnl ? {
                        label: `${pnlVal >= 0 ? '+' : ''}₹${pnlVal}`,
                        variant: pnlVal >= 0 ? 'green' : 'red',
                      } : {
                        label: 'Open',
                        variant: 'muted',
                      }}
                      fields={[
                        { label: 'Type', value: t.tradeType.replace(/_/g, ' '), bold: true },
                        { label: 'Option', value: t.optionType, accent: t.optionType === 'CE' ? 'green' : 'red', bold: true },
                        { label: 'Entry', value: `₹${t.entryPrice}` },
                        { label: 'Exit', value: t.exitPrice !== undefined ? `₹${t.exitPrice}` : '—' },
                      ]}
                    />
                  )
                })}
              </div>
            </MobileOnly>
          </>
        )}
      </div>

      {/* Panic Confirmation Modal */}
      {panicModal && (
        <div className="modal-overlay" onClick={() => setPanicModal(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 style={{ color: 'var(--red)' }}>⚠ Panic Exit</h3>
              <button className="modal-close" onClick={() => setPanicModal(null)}>✕</button>
            </div>
            <div className="modal-body">
              <p style={{ color: 'var(--text-secondary)', fontSize: '0.8125rem', marginBottom: '1rem' }}>
                Square off <strong>{panicModal.level === 'all' ? 'ALL POSITIONS' : (panicModal.target === 'rsi_straddle' ? 'RSI Straddle' : 'Trending Expiry') + ' positions'}</strong> immediately?
              </p>
              <div style={{ background: 'var(--red-glow)', padding: '0.75rem', borderRadius: 'var(--radius-sm)', border: '1px solid rgba(239,68,68,0.2)', fontSize: '0.8125rem' }}>
                This action will immediately square off all open positions. This cannot be undone.
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setPanicModal(null)}>Cancel</button>
              <button className="btn btn-red" onClick={handlePanicConfirm}>⚠ Confirm</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

